import React from 'react';

import { Button, Chip, Link, Stack, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import DebugModal from '~/components/DebugModal/DebugModal';

import PromoLink from '../PromoLink';

import { type PromoByToggleSearch, type PromoByToggleSearchResult } from './PromoByToggle';

type Props = {
  search?: PromoByToggleSearch;
  showMoreAvailable: boolean;
  onShowMore: () => void;
};

const PROMO_LOOKUP_PATH = '/marketing/search-promo';
const PROMO_REQUESTS_PATH = '/marketing/promo-requests';
const VOUCHER_LOOKUP_PATH = '/marketing/vouchers';

function getRelatedPageUrls(result: PromoByToggleSearchResult) {
  const requestsQuery = new URLSearchParams(
    result.entity === 'promo' ? { codeName: result.name } : { voucherName: result.name },
  );

  if (result.entity === 'voucher') {
    return {
      lookup: `${VOUCHER_LOOKUP_PATH}?${new URLSearchParams({ voucher_id: result.id })}#voucher-users`,
      requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
    };
  }

  return {
    lookup: `${PROMO_LOOKUP_PATH}?${new URLSearchParams({ name: result.name, page: '1', limit: '10' })}`,
    requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
  };
}

/** Displays results returned by {@link PromoByToggle}; it is deliberately separate so callers can place it as needed. */
export default function PromoByToggleResults({ search, showMoreAvailable, onShowMore }: Props) {
  if (!search) {
    return null;
  }

  const { entity, results } = search;

  if (results.length === 0) {
    return (
      <Typography role="status" aria-live="polite">
        No matching {entity === 'all' ? 'promo or voucher' : entity} results found.
      </Typography>
    );
  }

  const nameColumn: GridColDef<PromoByToggleSearchResult> = {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 240,
    renderCell: (params) =>
      params.row.entity === 'promo' ? (
        <PromoLink promoId={params.row.id} title={params.row.name} copyValue={params.row.name} />
      ) : (
        <CopyableField
          value={params.row.name}
          label={
            <Link href={`/marketing/vouchers/${encodeURIComponent(params.row.id)}`} target="_blank" rel="noreferrer">
              {params.row.name}
            </Link>
          }
        />
      ),
  };

  const refinedColumns: Array<GridColDef<PromoByToggleSearchResult>> = [
    nameColumn,
    {
      field: 'entity',
      headerName: 'Type',
      minWidth: 100,
      renderCell: (params) => <Chip label={params.row.entity === 'promo' ? 'Promo code' : 'Voucher'} size="small" />,
    },
    {
      field: 'relatedPages',
      headerName: 'Related pages',
      sortable: false,
      filterable: false,
      minWidth: 170,
      renderCell: (params) => {
        const relatedPageUrls = getRelatedPageUrls(params.row);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Link href={relatedPageUrls.lookup} target="_blank" rel="noreferrer">
              Lookup
            </Link>
            <Link href={relatedPageUrls.requests} target="_blank" rel="noreferrer">
              Requests
            </Link>
          </Stack>
        );
      },
    },
    {
      field: 'debug',
      headerName: 'Debug',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <DebugModal title="Debug" type="generic" data={params.row} buttonProps={{ size: 'small', variant: 'text' }} />
      ),
    },
  ];

  return (
    <Stack spacing={1}>
      <DataGrid
        aria-label={`${entity} toggle search results`}
        columns={refinedColumns}
        rows={results}
        getRowId={(row) => `${row.entity}:${row.id}`}
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        density="compact"
        rowHeight={40}
        sx={{ '& .MuiDataGrid-cell': { alignItems: 'center' } }}
      />
      {showMoreAvailable && (
        <Stack direction="row" justifyContent="center">
          <Button variant="text" onClick={onShowMore}>
            Show more
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
